/**
 * FilePreviewModal — in-browser preview for course material files.
 * PDFs, images, text, and HTML render natively in an iframe/img;
 * Office formats (docx, pptx, xlsx) render through the Microsoft Office
 * web viewer, which works because uploads are served from a public URL.
 * Everything else falls back to a download prompt.
 */
import React from 'react';
import Modal from './Modal';
import { analyzeLink } from '../../utils/embedProviders';

interface PreviewFile {
  title: string;
  file_name: string;
  /** Absolute URL to the stored file, or the link target for kind link */
  url: string;
  kind?: 'file' | 'link';
}

interface Props {
  file: PreviewFile | null;
  onClose: () => void;
}

type PreviewKind = 'iframe' | 'office' | 'image' | 'none';

const kindOf = (fileName: string): PreviewKind => {
  const ext = (fileName.split('.').pop() || '').toLowerCase();
  if (['pdf', 'txt', 'md', 'csv', 'html', 'htm'].includes(ext)) return 'iframe';
  if (['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(ext)) return 'office';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image';
  return 'none';
};

const FilePreviewModal: React.FC<Props> = ({ file, onClose }) => {
  if (!file) return null;
  let kind: PreviewKind = kindOf(file.file_name);
  if (file.kind === 'link') {
    const info = analyzeLink(file.url);
    kind = info.embedUrl ? 'iframe' : 'none';
    if (info.embedUrl) file = { ...file, url: info.embedUrl };
  }

  return (
    <Modal isOpen={true} onClose={onClose} title={file.title || file.file_name} size="xl">
      <div className="space-y-4">
        {kind === 'iframe' && (
          <iframe
            src={file.url}
            title={file.title}
            className="h-[70vh] w-full rounded-2xl border border-gray-200 bg-white dark:border-secondary-700"
          />
        )}
        {kind === 'office' && (
          <iframe
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.url)}`}
            title={file.title}
            className="h-[70vh] w-full rounded-2xl border border-gray-200 bg-white dark:border-secondary-700"
          />
        )}
        {kind === 'image' && (
          <div className="flex max-h-[70vh] items-center justify-center overflow-auto rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-secondary-700 dark:bg-secondary-900">
            <img src={file.url} alt={file.title} className="max-h-[65vh] max-w-full object-contain" />
          </div>
        )}
        {kind === 'none' && (
          <div className="flex flex-col items-center rounded-2xl border border-dashed border-gray-300 px-6 py-14 text-center dark:border-secondary-700">
            <svg className="h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="mt-4 text-sm font-semibold text-gray-600 dark:text-gray-300">
              {file.kind === 'link' ? 'This site cannot be embedded' : 'No preview for this file type'}
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {file.kind === 'link' ? 'Open it in a new tab instead.' : 'Download it to open with the right app.'}
            </p>
          </div>
        )}

        <div className="flex items-center justify-between">
          <p className="truncate text-xs font-medium text-gray-500 dark:text-gray-400">{file.file_name}</p>
          <a
            href={file.url}
            download={file.file_name}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-primary-600 px-4 py-2 text-xs font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
          >
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              {file.kind === 'link'
                ? <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                : <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />}
            </svg>
            {file.kind === 'link' ? 'Open in new tab' : 'Download'}
          </a>
        </div>
      </div>
    </Modal>
  );
};

export default FilePreviewModal;
